import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import { getColor, StyleVars, typeScale } from 'core/utils';
import { lighten } from 'polished';

const buttonHeights = { xl: '54px', lg: '60px', md: '44px', sm: '32px' };
const buttonFontSizes = { xl: '18px', lg: '16px', md: '16px', sm: '14px' };
const borderRadiusSizes = { xl: '8px', lg: '8px', md: '4px', sm: '2px' };

const hoverBackground = ({ themeType }) => {
	if (themeType === 'stroke') return getColor('neutral');
	if (themeType === 'antistroke') return getColor('concreteGray');

	return getColor(`${themeType}Light`);
};

const activeBackground = ({ themeType }) => {
	if (themeType === 'stroke') return transparentize(0.95, getColor('neutral'));
	if (themeType === 'antistroke') return transparentize(0.95, getColor('neutral'));

	return getColor(`${themeType}Dark`);
};

const btnLineHeight = ({ themeType, size }) => {
	const lineHeight = buttonHeights[size];
	if (themeType === "stroke" || themeType === "antistroke") {
		// substract the 2x border width
		let newLineHeight = Number(lineHeight.split("px")[0]) - 4;
		return `${newLineHeight}px`;
	}
	return lineHeight;
};

const btnBgColor = ({ themeType, disabled }) => {
	if (disabled) {
		if (themeType === "stroke") return getColor("neutralLight");
		if (themeType === "antistroke") return 'transparent';

		return getColor("inactive");
	}

	if (themeType === 'stroke') return 'transparent';
	if (themeType === 'antistroke') return 'transparent';

	return getColor(themeType);
};

const btnColor = ({ themeType, disabled }) => {
	if (disabled) return getColor('textFade');
	if (themeType === "stroke") return getColor("neutral");
	if (themeType === "antistroke") return getColor("neutral");
	return getColor("textLight");
};

const btnHoverColor = ({ themeType }) => {
	if (themeType === "antistroke") return getColor("black");
	return getColor("textLight");
};

const btnBorderRadius = ({ themeType, size }) => {
	if (themeType === "antistroke") return "4px";
	return borderRadiusSizes[size];
};

const btnBorder = ({ themeType, disabled }) => {
	if (themeType === "stroke") {
		if (disabled) return `2px solid ${getColor("inactive")}`;
		return `2px solid ${getColor("neutral")}`;
	}
	if (themeType === "antistroke") {
		if (disabled) return `2px solid ${lighten(0.4, getColor("cratejoyTeal"))}`;
		return `2px solid ${getColor("cratejoyTeal")}`;
	}

	return 0;
};

const activeColor = ({ themeType }) => {
	if (themeType === "stroke" || themeType === "antistroke") return getColor("black");
	return getColor("textLight");
};

const disabledBackgroundColor = ({ themeType }) => {
	if (themeType === "stroke") return getColor("textLight");
	if (themeType === "antistroke") return "transparent";
	return getColor("inactive");
};

const disabledBackgroundBorder = ({ themeType }) => {
	if (themeType === "stroke") return `2px solid ${getColor("inactive")}`;
	if (themeType === "antistroke") return `2px solid ${lighten(0.4, getColor("cratejoyTeal"))}`;
	return 0;
};

export const StyledButton = styled.button`
	display: inline-block;
	appearance: none;
	text-decoration: none;
	background-color: ${btnBgColor};
	border-radius: ${btnBorderRadius};
	color: ${btnColor};
	vertical-align: middle;
	padding: 0 16px;
	height: ${({ size }) => buttonHeights[size]};
	line-height: ${btnLineHeight};
	border: ${btnBorder};
	box-shadow: none;
	transition: all ease .85s;
	font-size: ${({ size }) => buttonFontSizes[size]};
	font-weight: 600;
	text-align: center;
	width: ${({ fullWidth }) => fullWidth ? '100%' : 'auto' };
	cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
	pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
	${({ themeType }) => themeType === "antistroke" && css`
		border-radius: 4px;
		font-size: 14px;
	`}

	&:hover, &:active, &:focus {
		text-decoration: none;
	}

	&:focus-visible: {
		box-shadow: 0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px #6366F1;
	}

	&:focus:not(:focus-visible) {
		outline: 0;
	}

	${({ disabled } ) => !disabled && css`
		&:hover {
			background-color: ${hoverBackground} ;
			color: ${btnHoverColor};

			svg.hasStroke {
				stroke: ${btnHoverColor};
			}
		}

		&:active, &:focus {
			background-color: ${activeBackground};
			color: ${activeColor};

			svg.hasStroke {
				stroke: ${activeColor};
			}
		}
	`}

	&.disabled {
		cursor: default;
		color: ${getColor('textFade')};
		background-color: ${disabledBackgroundColor};
		border: ${disabledBackgroundBorder};
	}
`;

const Button = ({ href, ...props }) => (
	<StyledButton as={href ? 'a': 'button'} href={href} {...props} />
);

Button.propTypes = {
	children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	onClick: PropTypes.func,
	size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
	themeType: PropTypes.oneOf(['positive', 'danger', 'purchase', 'neutral', 'stroke', 'gift', 'antistroke']),
	disabled: PropTypes.bool,
	fullWidth: PropTypes.bool,
	/**
		If `href` is specified, the element is displayed as an <a> instead of a <button>
	*/
	href: PropTypes.string,
	target: PropTypes.oneOf(['_self', '_target', '_blank', '_top'])
};

Button.defaultProps = {
	size: 'md',
	themeType: 'neutral',
	disabled: false,
	fullWidth: false,
};

export const ListingButton = styled(Button)`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
	padding: 0 16px;

	@media(max-width: ${StyleVars.screenSm}) {
		margin-bottom: ${typeScale(1)};
	}
`;

export default Button;
